import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Battery, Bed, AlertCircle, Zap, Heart } from "lucide-react";

const SLIDER_COLORS = {
  READY: "#4ade80", MODERATE: "#facc15", LOW: "#fb923c", REVIEW_REQUIRED: "#f87171",
};

const FIELDS = [
  { id: "energy",     label: "Energía",    icon: Battery, hint: "1 = agotado · 10 = pletórico" },
  { id: "sleep",      label: "Sueño",      icon: Bed,     hint: "1 = mal · 10 = descansado" },
  { id: "fatigue",    label: "Fatiga",     icon: Zap,     hint: "1 = fresco · 10 = muy cansado", inverse: true },
  { id: "discomfort", label: "Molestias",  icon: AlertCircle, hint: "1 = ninguna · 10 = dolor claro", inverse: true },
  { id: "motivation", label: "Motivación", icon: Heart,   hint: "1 = baja · 10 = a tope" },
];

export default function ClientReadiness() {
  const [answers, setAnswers] = useState({ energy: 7, sleep: 7, fatigue: 4, discomfort: 2, motivation: 7 });
  const [notes, setNotes] = useState("");
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/readiness?days=14");
      setHistory(data);
      const t = new Date().toISOString().slice(0, 10);
      const today_ = data.find((r) => r.date === t);
      if (today_) {
        setToday(today_);
        setAnswers({ energy: today_.energy, sleep: today_.sleep, fatigue: today_.fatigue, discomfort: today_.discomfort, motivation: today_.motivation });
        setNotes(today_.notes || "");
      }
    } catch (_) { /* si es 401 se maneja por interceptor */ }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/me/readiness", { ...answers, notes });
      setToday(data);
      toast.success("Registrado — sesión adaptada al estado de hoy");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <AppShell>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", margin: 0 }}>¿Cómo estás hoy?</h1>
        <p style={{ color: "#a3a3a3", fontSize: 14, margin: "4px 0 0" }}>2 minutos. Ayuda al coach a adaptar la sesión.</p>
      </div>

      {today && (
        <div className="qv-card-anim" style={{ background: `linear-gradient(90deg, ${SLIDER_COLORS[today.state]}15, transparent)`, border: `1px solid ${SLIDER_COLORS[today.state]}44`, padding: 16, borderRadius: 4, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <Sparkles size={16} color={SLIDER_COLORS[today.state]} />
            <span style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: SLIDER_COLORS[today.state], fontWeight: 700 }}>Estado {today.state}</span>
            <span style={{ marginLeft: "auto", fontFamily: "Bebas Neue, sans-serif", fontSize: 30, color: SLIDER_COLORS[today.state] }}>{today.score}<span style={{ fontSize: 14, color: "#a3a3a3" }}>/100</span></span>
          </div>
          {today.recommendations?.map((r, i) => (
            <div key={i} style={{ fontSize: 13, color: "#e5e5e5", marginTop: 4 }}>· {r}</div>
          ))}
        </div>
      )}

      <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", padding: 20, borderRadius: 4, marginBottom: 20 }}>
        {FIELDS.map((f) => {
          const Ic = f.icon;
          const val = answers[f.id];
          return (
            <div key={f.id} style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600 }}>
                  <Ic size={16} color="#a3a3a3" /> {f.label}
                </label>
                <span style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 22, color: "#dc2626" }}>{val}<span style={{ fontSize: 12, color: "#a3a3a3" }}>/10</span></span>
              </div>
              <input type="range" min={1} max={10} value={val}
                     onChange={(e) => setAnswers({ ...answers, [f.id]: parseInt(e.target.value) })}
                     className="qv-slider"
                     data-testid={`readiness-${f.id}`}
                     style={{ width: "100%" }} />
              <div style={{ fontSize: 11, color: "#737373", marginTop: 3 }}>{f.hint}</div>
            </div>
          );
        })}
        <label style={{ display: "block", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 6 }}>Notas (opcional)</label>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Algo que el coach deba saber..."
                  style={{ width: "100%", background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "9px 12px", fontSize: 14, borderRadius: 3, outline: "none", boxSizing: "border-box", resize: "vertical" }} />
        <button onClick={submit} disabled={saving} data-testid="readiness-submit"
                className="qv-btn-primary"
                style={{ marginTop: 14, width: "100%", background: "#dc2626", color: "#fff", border: "none", padding: "14px", fontSize: 14, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer", borderRadius: 2 }}>
          {saving ? "Guardando…" : today ? "Actualizar" : "Registrar"}
        </button>
      </div>

      {history.length > 0 && (
        <div>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 10 }}>Últimos 14 días</div>
          <div style={{ display: "flex", gap: 4, height: 60, alignItems: "flex-end" }}>
            {history.slice(0, 14).reverse().map((r) => (
              <div key={r.date} title={`${r.date} · ${r.state} · ${r.score}`}
                   style={{ flex: 1, background: SLIDER_COLORS[r.state], height: `${r.score}%`, minHeight: 6, borderRadius: 2, transition: "height .3s ease" }} />
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}
